import { Button } from '@pwc/pwc-deals-ui';
import { useTranslation } from 'react-i18next';
import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useLayoutEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { Link, useParams, useHistory } from 'react-router-dom';
import {
  getProjectDisclaimerSelector,
  acceptProjectDisclaimerThunk,
} from './projectDisclaimerSlice';
import './ProjectDisclaimerView.scss';
import {
  getDistanceLeftToScroll,
  getDistanceLeftToScrollFromWindow,
  getScrollableAmount,
} from '../../common/services/scrollDataService';
import Separator from '../../common/components/Separator';
import { sanitize } from '../../common/services/sanitizeService';

const ProjectDisclaimerView = () => {
  const { t } = useTranslation();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const projectDisclaimer = useSelector(
    getProjectDisclaimerSelector(projectId)
  );
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasScrollableText, setHasScrollableText] = useState(false);
  const [
    waitingForDisclaimerAcceptanceCall,
    setWaitingForDisclaimerAcceptanceCall,
  ] = useState(false);
  const textRef = useRef(null);

  const handleTextScroll = useMemo(() => {
    return (e) => {
      const acceptableMarginOfError = 5;
      const distanceLeftToScroll = getDistanceLeftToScroll(e.target);
      if (
        hasScrollableText &&
        distanceLeftToScroll <= acceptableMarginOfError
      ) {
        setHasScrolledToBottom(true);
      }
    };
  }, [hasScrollableText]);

  const handleTextScrollMobile = useMemo(() => {
    return () => {
      if (
        !hasScrollableText &&
        getDistanceLeftToScrollFromWindow(textRef.current) <= 0
      ) {
        setHasScrolledToBottom(true);
      }
    };
  }, [hasScrollableText]);

  const updateHasScrollableText = useMemo(() => {
    return () => {
      const acceptableMarginOfError = 50;
      const amountCanScroll = getScrollableAmount(textRef.current);
      setHasScrollableText(amountCanScroll > acceptableMarginOfError);
    };
  }, []);

  useLayoutEffect(() => {
    if (textRef.current) {
      updateHasScrollableText();
      window.addEventListener('resize', updateHasScrollableText);
      return () =>
        window.removeEventListener('resize', updateHasScrollableText);
    }
    return undefined;
  }, [updateHasScrollableText]);

  useEffect(() => {
    if (textRef.current) {
      window.addEventListener('scroll', handleTextScrollMobile);
      return () => window.removeEventListener('scroll', handleTextScrollMobile);
    }
    return undefined;
  }, [handleTextScrollMobile]);

  useEffect(() => {
    const acceptableMarginOfError = 5;

    if (
      textRef &&
      textRef.current &&
      getDistanceLeftToScroll(textRef.current) < acceptableMarginOfError
    ) {
      setHasScrolledToBottom(true);
    }
  }, [textRef, projectDisclaimer]);

  useEffect(() => {
    if (waitingForDisclaimerAcceptanceCall && projectDisclaimer.hasAccepted) {
      const to = history.location.state
        ? history.location.state.from
        : `/projects/${projectId}`;

      history.replace(to);
    }
  }, [
    waitingForDisclaimerAcceptanceCall,
    projectDisclaimer,
    history,
    projectId,
  ]);

  if (waitingForDisclaimerAcceptanceCall) {
    return null;
  }

  const acceptDisclaimer = () => {
    dispatch(acceptProjectDisclaimerThunk(projectId));
    setWaitingForDisclaimerAcceptanceCall(true);
  };

  return projectDisclaimer && !projectDisclaimer.isLoading ? (
    <div className="project-disclaimer-container">
      <div
        className="project-disclaimer-title"
        data-testid="project-disclaimer-title"
      >
        {t('projectDisclaimer')}
      </div>
      <div
        data-testid="project-disclaimer"
        className="project-disclaimer"
        ref={textRef}
        onScroll={handleTextScroll}
      >
        {projectDisclaimer && !projectDisclaimer.hasAccepted && !isPwcUser && (
          <>
            <div className="project-disclaimer-prompt">
              {t('projectAccessMessage')}
            </div>
            <Separator />
          </>
        )}
        <div
          data-testid="project-disclaimer-content"
          className="project-disclaimer-content"
          dangerouslySetInnerHTML={{
            __html: sanitize(projectDisclaimer.disclaimerHtml),
          }}
        />
      </div>
      {!isPwcUser && !projectDisclaimer.hasAccepted && (
        <div className="project-disclaimer-actions">
          <Link to="/projects" data-testid="cancel-button">
            {t('cancelDisclaimer')}
          </Link>
          <Button
            data-testid="accept-button"
            type="button"
            disabled={!hasScrolledToBottom}
            onClick={acceptDisclaimer}
          >
            {t('acceptProceed')}
          </Button>
        </div>
      )}
    </div>
  ) : null;
};

export default ProjectDisclaimerView;
